import {
  isAdministrationAssignment,
  isOperationsDepartment,
} from "./orgHelpers";

function activePeople(employees) {
  return (employees || []).filter((emp) => emp && emp.status !== "terminated");
}

function belongsToDepartment(emp, dept) {
  if (!emp || !dept) return false;
  if (emp.departmentId === dept.id) return true;
  return Boolean(dept.backendId && emp.departmentBackendId === dept.backendId);
}

function belongsToBranch(emp, branch) {
  if (!emp || !branch) return false;
  if (emp.branchId === branch.id) return true;
  return Boolean(branch.backendId && emp.branchBackendId === branch.backendId);
}

function node(id, title, subtitle, kind, children = []) {
  return {
    id: String(id),
    title: title || "—",
    subtitle: subtitle || "",
    kind,
    children: children.filter(Boolean),
  };
}

function peopleNodes(id, people) {
  const list = (people || []).filter(Boolean);
  if (!list.length) return [];
  if (list.length <= 4) {
    return list.map((emp) =>
      node(
        `emp-${emp.id}`,
        emp.name || "موظف",
        emp.jobTitle || "موظف",
        "person",
      ),
    );
  }
  const preview = list
    .slice(0, 2)
    .map((emp) => emp.name)
    .filter(Boolean)
    .join("، ");
  return [node(id, `${list.length} موظف`, preview, "group")];
}

function reportsTo(staff, managerId) {
  const rootId = String(managerId);
  const ids = new Set([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    staff.forEach((emp) => {
      const id = String(emp.id);
      if (ids.has(id)) return;
      if (emp.managerId && ids.has(String(emp.managerId))) {
        ids.add(id);
        grew = true;
      }
    });
  }
  return staff.filter((emp) => ids.has(String(emp.id)) && String(emp.id) !== rootId);
}

function buildStaffLeaves(staff, useSupervisors) {
  if (!useSupervisors) return peopleNodes("staff", staff);
  const supers = staff.filter((emp) => emp.memberLevel === "supervisor");
  const taken = new Set();
  const nodes = supers.map((sup) => {
    taken.add(String(sup.id));
    const team = reportsTo(staff, sup.id).filter(
      (emp) => emp.memberLevel !== "department-manager",
    );
    team.forEach((emp) => taken.add(String(emp.id)));
    return node(
      `sup-${sup.id}`,
      sup.name || "مشرف",
      sup.jobTitle || "مشرف",
      "person",
      peopleNodes(`team-${sup.id}`, team),
    );
  });
  const rest = staff.filter(
    (emp) =>
      emp.memberLevel === "employee" && !taken.has(String(emp.id)),
  );
  return [...nodes, ...peopleNodes("staff-rest", rest)];
}

function buildSectionNodes(staff, structure) {
  const managers = staff.filter((emp) => emp.memberLevel === "section-manager");
  const used = new Set();
  const useSupervisors = structure.ranks?.includes("supervisor");
  const nodes = managers.map((manager) => {
    used.add(String(manager.id));
    const team = reportsTo(staff, manager.id);
    team.forEach((emp) => used.add(String(emp.id)));
    const title =
      manager.jobTitle && manager.jobTitle !== "مدير قسم"
        ? manager.jobTitle
        : "قسم";
    return node(
      `sec-${manager.id}`,
      title,
      manager.name || "مدير قسم",
      "section",
      buildStaffLeaves(team, useSupervisors),
    );
  });

  const leftover = staff.filter(
    (emp) =>
      emp.memberLevel !== "department-manager" &&
      emp.memberLevel !== "regional-manager" &&
      emp.memberLevel !== "section-manager" &&
      !used.has(String(emp.id)),
  );
  if (leftover.length) {
    nodes.push(
      node("sec-rest", "بدون قسم", "", "group", peopleNodes("sec-rest-people", leftover)),
    );
  }
  return nodes;
}

function buildBranchNode(branch, staff, structure) {
  const here = staff.filter((emp) => belongsToBranch(emp, branch));
  const manager = here.find((emp) => emp.memberLevel === "branch-manager");
  const taken = new Set();
  if (manager) taken.add(String(manager.id));
  const useSupervisors = structure.ranks?.includes("supervisor");
  const supers = useSupervisors
    ? here.filter((emp) => emp.memberLevel === "supervisor")
    : [];
  const supNodes = supers.map((sup) => {
    taken.add(String(sup.id));
    const team = here.filter(
      (emp) =>
        emp.memberLevel === "employee" && String(emp.managerId) === String(sup.id),
    );
    team.forEach((emp) => taken.add(String(emp.id)));
    return node(
      `sup-${branch.id}-${sup.id}`,
      sup.name || "مشرف",
      sup.jobTitle || "مشرف",
      "person",
      peopleNodes(`team-${branch.id}-${sup.id}`, team),
    );
  });
  const rest = here.filter(
    (emp) =>
      !taken.has(String(emp.id)) &&
      emp.memberLevel !== "regional-manager" &&
      emp.memberLevel !== "department-manager" &&
      emp.memberLevel !== "branch-manager",
  );
  return node(
    `br-${branch.id}`,
    branch.name || "فرع",
    manager?.name || "بدون مدير فرع",
    "branch",
    [...supNodes, ...peopleNodes(`br-staff-${branch.id}`, rest)],
  );
}

function branchIsManaged(branch, managed) {
  return (managed || []).some(
    (item) =>
      item &&
      (item.id === branch.id ||
        item.id === branch.backendId ||
        (item.name && item.name === branch.name)),
  );
}

function buildOpsNodes(staff, branches, structure) {
  const fieldBranches = (branches || []).filter(
    (branch) => branch && !isAdministrationAssignment(branch.id),
  );
  const nodes = [];

  if (structure.usesSections) {
    const hq = staff.filter(
      (emp) =>
        emp.memberLevel !== "department-manager" &&
        emp.memberLevel !== "regional-manager" &&
        emp.memberLevel !== "branch-manager" &&
        isAdministrationAssignment(emp.branchId),
    );
    const sectionNodes = buildSectionNodes(hq, structure);
    if (sectionNodes.length) {
      nodes.push(node("hq", "مقر التشغيل", "", "section", sectionNodes));
    }
  }

  const useRegions = Boolean(structure.ranks?.includes("regional-manager"));
  if (!useRegions) {
    return [
      ...nodes,
      ...fieldBranches.map((branch) => buildBranchNode(branch, staff, structure)),
    ];
  }

  const regions = staff.filter((emp) => emp.memberLevel === "regional-manager");
  const claimed = new Set();
  regions.forEach((region) => {
    const regionBranches = fieldBranches.filter((branch) => {
      const hit = branchIsManaged(branch, region.managedBranches);
      if (hit) claimed.add(String(branch.id));
      return hit;
    });
    nodes.push(
      node(
        `region-${region.id}`,
        region.name || "مدير منطقة",
        "مدير منطقة",
        "region",
        regionBranches.map((branch) => buildBranchNode(branch, staff, structure)),
      ),
    );
  });

  const loose = fieldBranches.filter((branch) => !claimed.has(String(branch.id)));
  const looseNodes = loose.map((branch) => buildBranchNode(branch, staff, structure));
  if (regions.length && looseNodes.length) {
    nodes.push(node("region-none", "بدون منطقة", "", "region", looseNodes));
  } else {
    nodes.push(...looseNodes);
  }
  return nodes;
}

function buildDepartment(dept, structure, people, branches) {
  const staff = people.filter(
    (emp) => belongsToDepartment(emp, dept) && emp.memberLevel !== "gm",
  );
  const managers = staff.filter((emp) => emp.memberLevel === "department-manager");
  const ops = Boolean(structure.usesBranches) && isOperationsDepartment(dept.id);
  let children = [];
  if (ops) {
    children = buildOpsNodes(staff, branches, structure);
  } else if (structure.usesSections && structure.ranks?.includes("section-manager")) {
    children = buildSectionNodes(staff, structure);
  } else {
    const rest = staff.filter((emp) => emp.memberLevel !== "department-manager");
    children = buildStaffLeaves(rest, structure.ranks?.includes("supervisor"));
  }

  return node(
    `dept-${dept.id}`,
    dept.name || "إدارة",
    managers.map((emp) => emp.name).filter(Boolean).join("، ") || "بدون مدير إدارة",
    "department",
    children,
  );
}

export function buildOrgChartTree({
  structure,
  departments = [],
  branches = [],
  employees = [],
} = {}) {
  const people = activePeople(employees);
  const gm = people.find((emp) => emp.memberLevel === "gm");
  const depts = (departments || []).filter((dept) => dept.isActive !== false);
  return node(
    "gm",
    gm?.name || "المدير العام",
    "المدير العام",
    "gm",
    depts.map((dept) => buildDepartment(dept, structure || {}, people, branches)),
  );
}

export function collectOrgChartTitles(tree) {
  const titles = [];
  function walk(current) {
    if (!current) return;
    titles.push(current.title);
    (current.children || []).forEach(walk);
  }
  walk(tree);
  return titles;
}
